const CANONICAL_UNSIGNED_DECIMAL = /^(?:0|[1-9][0-9]*)$/;
const U64_MAX_DECIMAL = "18446744073709551615";

export function assertUnsignedDecimal(value: string): string {
  if (
    typeof value !== "string"
    || value.length > U64_MAX_DECIMAL.length
    || !CANONICAL_UNSIGNED_DECIMAL.test(value)
    || (value.length === U64_MAX_DECIMAL.length && value > U64_MAX_DECIMAL)
  ) {
    throw new RangeError("Expected a canonical unsigned 64-bit decimal string.");
  }
  return value;
}

function asUnsignedBigInt(value: string): bigint {
  return BigInt(assertUnsignedDecimal(value));
}

export function compareUnsignedDecimal(left: string, right: string): number {
  const leftValue = asUnsignedBigInt(left);
  const rightValue = asUnsignedBigInt(right);
  if (leftValue < rightValue) return -1;
  if (leftValue > rightValue) return 1;
  return 0;
}

export function maxUnsignedDecimal(first: string, ...rest: string[]): string {
  return rest.reduce(
    (maximum, value) => compareUnsignedDecimal(value, maximum) > 0 ? value : maximum,
    assertUnsignedDecimal(first),
  );
}

export function previousUnsignedDecimal(value: string): string {
  const parsed = asUnsignedBigInt(value);
  return parsed === 0n ? "0" : String(parsed - 1n);
}

export function clampUnsignedDecimal(value: string, maximum: string): string {
  return compareUnsignedDecimal(value, maximum) <= 0
    ? assertUnsignedDecimal(value)
    : assertUnsignedDecimal(maximum);
}

export function saturatingSubtractUnsignedDecimal(
  minuend: string,
  subtrahend: string,
): string {
  const minuendValue = asUnsignedBigInt(minuend);
  const subtrahendValue = asUnsignedBigInt(subtrahend);
  return minuendValue > subtrahendValue
    ? String(minuendValue - subtrahendValue)
    : "0";
}

function assertRatioPart(value: number, options: { allowZero: boolean }): bigint {
  if (!Number.isSafeInteger(value) || value < (options.allowZero ? 0 : 1)) {
    throw new RangeError("Expected a bounded integer ratio.");
  }
  return BigInt(value);
}

export function isUnsignedDecimalRatioAtLeast(
  used: string,
  included: string,
  numerator: number,
  denominator: number,
): boolean {
  const numeratorValue = assertRatioPart(numerator, { allowZero: true });
  const denominatorValue = assertRatioPart(denominator, { allowZero: false });
  return asUnsignedBigInt(used) * denominatorValue
    >= asUnsignedBigInt(included) * numeratorValue;
}

export function clampedUnsignedDecimalPercent(used: string, included: string): number {
  const includedValue = asUnsignedBigInt(included);
  if (includedValue === 0n) return 0;
  const hundredths = (asUnsignedBigInt(used) * 10_000n) / includedValue;
  const clampedHundredths = hundredths > 10_000n ? 10_000n : hundredths;
  return Number(clampedHundredths) / 100;
}

export function formatUnsignedDecimal(
  value: string,
  locales?: Intl.LocalesArgument,
): string {
  return asUnsignedBigInt(value).toLocaleString(locales);
}
